import { useEffect } from 'react'
import {
  ConfigurableProduct,
  MediaGalleryInterface,
  usePdpQuery,
  useProductPriceQuery,
} from '@magentoTypes'
import { useRouter } from 'next/router'
import { createMagentoClient } from 'graphql/magentoClient'
import { useQueryClient } from '@tanstack/react-query'
import { Description } from './components/Description'
import { Gallery } from './components/Gallery'
import { Sidebar } from './components/Sidebar'
import { VideoPlayer } from 'elements/VideoPlayer/VideoPlayer'
import { ClientOnly } from '~/elements/ClientOnly/ClientOnly'
import Instagram from './components/Instagram'
import { useProductAttributes } from '~/hooks/useProductAttributes'
import { DataLayerEnum, useGTM } from '~/hooks/useGTM'
import { PageType, useSEO } from '~/hooks/useSEO'
import { NextSeo } from 'next-seo'
import { createLanguageRegionLocale } from '~/lib/createLanguageRegionLocale'
import { DEFAULT_LOCALE } from '~/config/constants'
import { useBrandType } from '~/hooks/useBrandType'
import { getRelatedSkus } from '../PDP/lib/getRelatedSkus'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import ReviewsWidget from '../ClientReviews/ReviewsWidget'
import { CioRecommendations } from '../CioRecommendations/CioRecommendations'
import { WebsisteJsonLd } from '../StructuredData/WebsiteJsonLd'
import { JsonLdScript } from '../StructuredData/JsonLdScript'
import clsx from 'clsx'

const LazyProductRatingLoader = dynamic(
  () => import('../ClientReviews/components/ProductRatingLoader'),
  { ssr: false },
)

type Props = {
  urlKey: string
  structuredData: string
}

export const PDP = ({ urlKey, structuredData }: Props) => {
  const queryClient = useQueryClient()
  const router = useRouter()
  const finalLocale = router.locale ? createLanguageRegionLocale(router.locale) : DEFAULT_LOCALE
  const magentoClient = createMagentoClient({ usePost: false, queryClient, locale: finalLocale })
  const { data: pdpData } = usePdpQuery(magentoClient, { urlKey })
  const { data: productPriceData } = useProductPriceQuery(magentoClient, { urlKey })
  const { t } = useTranslation()
  const { dataLayer } = useGTM()
  const { findLabel } = useProductAttributes()
  const product = pdpData?.products?.items?.[0] as ConfigurableProduct
  const productPrice = productPriceData?.products?.items?.[0] as ConfigurableProduct

  const { canonical, languageAlternates } = useSEO({
    identifier: product?.id,
    type: PageType.MAGENTO_PRODUCT_PAGE,
  })

  useEffect(() => {
    dataLayer(DataLayerEnum.PAGE, 'product')
  }, [dataLayer])

  useEffect(() => {
    if (product && productPrice) {
      const possibleVariants = product.variants
        ?.filter(
          (variant) =>
            variant?.attributes?.[0]?.value_index !== 11932 &&
            variant?.attributes?.[0]?.value_index !== 10771,
        )
        .map((variant) => {
          return {
            item_id_possible: variant?.product?.sku,
            item_variant_possible: variant?.attributes?.[0]?.label,
          }
        })
      dataLayer(DataLayerEnum.EVENT, 'view_item', {
        ecommerce: {
          currency: productPrice?.price_range?.minimum_price?.final_price.currency || 'EUR',
          items: [
            {
              item_id: product.sku,
              item_variants_possible: possibleVariants || [],
              item_name: product.name,
              item_brand: findLabel({
                attributeCode: 'manufacturer',
                attributeValue: product.manufacturer as number,
              }),
              category: '',
            },
          ],
        },
      })
    }
  }, [product, productPrice, dataLayer, findLabel])

  const upsellSkus = product?.upsell_products?.map((v) => v?.sku as string)
  const brandType = useBrandType({ manufacturer: product?.manufacturer as number })

  const generatePageMetaTitle = (product: ConfigurableProduct) => {
    const isRugProduct = product?.attribute_set_id === 115
    return isRugProduct
      ? t('Discover {{Placeholder_Product_Name}} in various sizes', {
          Placeholder_Product_Name: product?.name || '',
        })
      : t('Buy {{Placeholder_Product_Name}} | benuta', {
          Placeholder_Product_Name: product?.name || '',
        })
  }

  const generatePageMetaDescription = (product: ConfigurableProduct) => {
    return t(
      '{{Placeholder_Product_Name}} for {{Placeholder_Product_Rooms}} ✓ Fast Shipping ►Buy Now!',
      {
        Placeholder_Product_Name: product?.name || '',
        Placeholder_Product_Rooms: findLabel({
          attributeCode: 'benuta_living_area',
          attributeValue: product?.benuta_living_area as string,
        }),
      },
    )
  }

  const relatedSkus = getRelatedSkus(product)

  return (
    <>
      <NextSeo
        canonical={canonical}
        languageAlternates={languageAlternates}
        title={generatePageMetaTitle(product)}
        description={generatePageMetaDescription(product)}
        openGraph={{
          type: 'Website',
          description: generatePageMetaDescription(product),
          title: generatePageMetaTitle(product),
          images: [
            {
              url: (product?.media_gallery?.[0]?.url + '?width=1200&amp;quality=70') as string,
              alt: generatePageMetaDescription(product),
              type: 'image/jpg',
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
          site: '@benuta',
          handle: '@benuta',
        }}
      />
      <div className={clsx('content-width pt-[var(--header-padding)]')}>
        {brandType && (
          <menu className="hidden w-full flex-wrap items-center gap-4 py-2.5 pl-20 text-[12px] !text-primary-black lg:block">
            <li>
              <Link href="/">{t('Our brands')}</Link>
              <span className="mx-2">{'>'}</span>
              <Link href={brandType.route}>{brandType.name}</Link>
            </li>
          </menu>
        )}
        <article className="grid grid-cols-1 lg:grid-cols-[1fr_488px]">
          <Gallery
            className="relative col-start-1"
            gallery={product?.media_gallery as [MediaGalleryInterface]}
            product={product}
            productPrice={productPrice}
          />
          <Sidebar product={product} productPrice={productPrice} />
          <Description className="relative col-start-1 mb-6 lg:mb-0" product={product} />
          {product?.video_url ? (
            <div className="relative col-start-1">
              <ClientOnly>
                <div className="relative col-span-1 aspect-[16/9] h-full w-full max-w-full overflow-hidden">
                  <div
                    id="product-video"
                    className="invisible absolute top-[-220px] h-[220px] lg:top-[-184px] lg:h-[184px]"
                  ></div>
                  <VideoPlayer
                    hidePlayButton
                    src={product?.video_url}
                    placeholderUrl={''}
                    placeholderAlt={'video alt'}
                    playButtonClass="flex justify-center items-center w-20 h-20 lg:w-24 lg:h-24 bg-primary-white rounded-full"
                    placeholderSizes="(min-width: 1024px) 80vw, 100vw"
                  />
                </div>
              </ClientOnly>
            </div>
          ) : null}
        </article>
        {product.sku && (
          <CioRecommendations
            className="pt-[50px] pb-[50px] lgx:pt-[100px]"
            podId="pdp-also-like"
            headline={t('You might also like')}
            params={{ itemIds: product.sku }}
          />
        )}
        {product.sku && (
          <CioRecommendations
            className="pt-[50px] pb-[50px]"
            podId="pdp-also-purchased"
            headline={t('Customers also purchased')}
            params={{ itemIds: product.sku }}
          />
        )}
        <Instagram sku={product?.sku as string} />
        <CioRecommendations
          className="pt-[50px] pb-[50px] lgx:pt-[100px]"
          podId="pdp-recently-viewed"
          headline={t('Recently viewed')}
        />
        <ReviewsWidget relatedSkus={relatedSkus} />
        <LazyProductRatingLoader sku={product.sku} enabled={router.isReady} forPDP />
        <WebsisteJsonLd />
        {structuredData && <JsonLdScript id="product-json-ld" content={structuredData} />}
      </div>
    </>
  )
}
export default PDP
